import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { merge, pipe, switchMap } from 'rxjs';
import { PackageWorkflowVersion, WorkflowName, WorkflowStateDictionary, WorkflowStateUiMappings } from '../models/workflows';
import { WorkflowEngineService } from '../services/workflows/workflow-enginge.service';

export interface DictionaryState {
  workflowDictionary: WorkflowStateDictionary;
  mappingErrors: Record<string, string>;
}

const initialState: DictionaryState = {
  mappingErrors: {},
  workflowDictionary: {
    damage_package_workflow: { v6: {}, v10: {} },
    customer_claim_workflow: {},
    damage_manual_workflow: {},
  },
};

export const DictionaryStore = signalStore(
  {providedIn: 'root'},
  withState(initialState),
  withMethods((store, workflowEngineService = inject(WorkflowEngineService)) => {
    const loadMapping = (name: WorkflowName, version?: PackageWorkflowVersion) =>
      workflowEngineService.loadWorkflowUiMappings(name, version).pipe(tapResponse({
        next: response => {
          const mapping = transformWorkflowMapping(response);
          const dictionary = store.workflowDictionary();
          const key = version ? `${name}:${version}` : name;
          const mappingErrors = { ...store.mappingErrors() };
          delete mappingErrors[key];
          patchState(store, {
            mappingErrors,
            workflowDictionary: name === 'damage_package_workflow' && version
              ? { ...dictionary, damage_package_workflow: { ...dictionary.damage_package_workflow, [version]: mapping } }
              : { ...dictionary, [name]: mapping },
          });
        },
        error: () => {
          const key = version ? `${name}:${version}` : name;
          patchState(store, { mappingErrors: { ...store.mappingErrors(), [key]: 'Workflow mapping unavailable' } });
        },
      }));
    return {
      loadWorkflowDictionary: rxMethod<void>(pipe(switchMap(() => merge(
        loadMapping('damage_package_workflow', 'v6'),
        loadMapping('damage_package_workflow', 'v10'),
        loadMapping('customer_claim_workflow'),
        loadMapping('damage_manual_workflow'),
      )))),
    };
  })
);

function transformWorkflowMapping(workflows: WorkflowStateUiMappings): Record<string, string> {
  const transformedMapping: Record<string, string> = {};
  workflows.workflow_state.forEach(workflowState => {
    transformedMapping[workflowState.workflow_state] = workflowState.display_name;
  });
  return transformedMapping;
}
